import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { UserContactInformationAccordionSchema } from '../../../schemas/userSchema';
import { updateUserProfileInfo } from '../updateUserProfileInfo';
import { usePageLoader } from '../../../context/pageLoader';
import useIonicToast from '../../../hooks/useIonicToast';
import z from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { toYmd } from '../../../utils/helperFunction/toYmd';

type FormData = z.infer<typeof UserContactInformationAccordionSchema>;

export interface UseUserContactInformationServiceReturn {
  control: ReturnType<typeof useForm<FormData>>['control'];
  handleSubmit: ReturnType<typeof useForm<FormData>>['handleSubmit'];
  reset: ReturnType<typeof useForm<FormData>>['reset'];
  isDirty: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const useUserContactInformationService =
  (): UseUserContactInformationServiceReturn => {
    const clientQuery = useQueryClient();
    const invalidateProfile = () =>
      clientQuery.invalidateQueries({ queryKey: ['user-profile'] });

    // Get user information from Redux
    const userInfo = useSelector(
      (state: RootState) => state.userProfile?.extended_user?.meta_data
    );

    const { signedAccessToken: AuthToken } = useSelector(
      (state: RootState) => state.userSession
    );

    // Initialize form with validation
    const {
      control,
      handleSubmit,
      reset,
      formState: { isDirty },
    } = useForm<FormData>({
      resolver: zodResolver(UserContactInformationAccordionSchema),
      mode: 'onChange',
      defaultValues: {
        primary_information: {
          middle_name: undefined,
          secondary_email: undefined,
          secondary_phone: undefined,
          additional_info: undefined,
        },
        secondary_information: {
          user_name: undefined,
          website: undefined,
          short_bio: undefined,
        },
      },
    });

    // Get UI utilities
    const { setBusy } = usePageLoader();
    const { presentIonToast } = useIonicToast();

    // Populate form with user data when it changes
    useEffect(() => {
      if (userInfo) {
        reset({
          primary_information: {
            middle_name:
              userInfo?.primary_information?.middle_name || undefined,
            // Coerce stored value (which may include a trailing time component
            // like "2026-01-06T15:14:00") down to YYYY-MM-DD so the <input
            // type="date"> control accepts it as a valid initial value.
            date_of_birth:
              toYmd(userInfo?.primary_information?.date_of_birth) || undefined,
            secondary_email:
              userInfo?.primary_information?.secondary_email || undefined,
            secondary_phone:
              userInfo?.primary_information?.secondary_phone || undefined,
            additional_info:
              userInfo?.primary_information?.additional_info || undefined,
          },
          secondary_information: {
            user_name: userInfo?.secondary_information?.user_name || undefined,
            website: userInfo?.secondary_information?.website || undefined,
            short_bio: userInfo?.secondary_information?.short_bio || undefined,
          },
        });
      }
    }, [userInfo, reset]);

    // Handle form submission
    const onSubmit = handleSubmit(async (formData: FormData) => {
      if (!AuthToken || !isDirty) return;
      try {
        setBusy(true);
        // Normalize DOB to YYYY-MM-DD on the wire so downstream consumers
        // (Sterling KYC, completion calculator, etc.) get a clean date.
        const normalized = {
          ...formData,
          primary_information: {
            ...formData.primary_information,
            date_of_birth: toYmd(formData.primary_information?.date_of_birth),
          },
        };
        await updateUserProfileInfo(normalized as any, AuthToken);
        presentIonToast('Profile successfully updated');
        invalidateProfile();
      } catch (error: any) {
        presentIonToast('Profile update failed', 'error');
        console.error(error.message);
      } finally {
        setBusy(false);
      }
    });

    return {
      control,
      handleSubmit,
      reset,
      isDirty,
      onSubmit,
    };
  };
